import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, Home, Shield, Sparkles, CheckCircle, Loader2, AlertCircle, FlaskConical, Ticket, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { cleanSlateProtocol } from '../../../utils/missionDataManager';

const PACK_TYPE_TO_TUNNEL_SLUG = {
  mission_demenagement: 'demenagement',
  mission_heritage: 'succession',
  mission_audit: 'audit',
  // CCC.15 : bundle entre par le tunnel Déménagement (point d'entrée commun).
  // Le backend crée 2 missions liées par bundle_id, l'audit se déclenche en fin de tunnel.
  mission_bundle_demenagement_audit: 'demenagement',
};

const PaymentTab = ({ user, onPaymentSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [bypassLoading, setBypassLoading] = useState(null);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [showCgvError, setShowCgvError] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [pricing, setPricing] = useState(null);
  // CCC.13 : toggle "+ Audit (+20€)" sur la carte Déménagement (au lieu de carte bundle séparée)
  const [addAuditToDemenagement, setAddAuditToDemenagement] = useState(false);
  const isAdmin = user?.email?.toLowerCase() === 'leffaceur@effaceurpro.fr';

  useEffect(() => {
    axios.get(`${API}/packs/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const handleValidateCoupon = async (packType = null) => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error('Saisissez un code'); return; }
    setCouponValidating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/coupons/validate`, {
        code, pack_type: packType || 'mission_demenagement',
      }, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
      if (res.data.valid) {
        setAppliedCoupon({
          code: res.data.code, percent_off: res.data.percent_off,
          description: res.data.description, type: res.data.type,
        });
        toast.success(`Code "${res.data.code}" appliqué : -${res.data.percent_off}%`);
      } else {
        setAppliedCoupon(null);
        toast.error(res.data.reason || 'Code invalide');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de validation');
    } finally { setCouponValidating(false); }
  };

  const handleRemoveCoupon = () => { setAppliedCoupon(null); setCouponInput(''); };

  const handleBypassPayment = async (packType) => {
    setBypassLoading(packType);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/payments/admin-bypass`, { pack_type: packType },
        { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
      if (res.data.status === 'success') {
        toast.success(`Pack "${res.data.pack_name}" active (Mode Test)`);
        const tunnelSlug = PACK_TYPE_TO_TUNNEL_SLUG[packType];
        if (tunnelSlug && tunnelSlug !== 'audit') {
          cleanSlateProtocol(tunnelSlug, null);
          navigate(`/missions/${tunnelSlug}?new=true`);
        } else { onPaymentSuccess(); }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la simulation');
    } finally { setBypassLoading(null); }
  };

  const handleSelectPack = async (packType) => {
    // Pack Audit : pas de paiement, redirection directe vers /audit
    if (packType === 'mission_audit') {
      navigate('/audit');
      return;
    }
    // CCC.13 : si toggle "+Audit" activé sur Déménagement → bundle 169€
    if (packType === 'mission_demenagement' && addAuditToDemenagement) {
      packType = 'mission_bundle_demenagement_audit';
    }
    if (!isAdmin && !cgvAccepted) {
      setShowCgvError(true);
      toast.error('Veuillez accepter les CGV pour continuer');
      document.getElementById('cgv-checkbox-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setLoading(packType);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/payments/checkout`, {
        pack_type: packType, origin_url: window.location.origin,
        cgv_accepted: cgvAccepted, cgv_accepted_at: new Date().toISOString(),
        coupon_code: appliedCoupon?.code || ''
      }, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });

      const tunnelSlug = PACK_TYPE_TO_TUNNEL_SLUG[packType];
      const enterFreshTunnel = () => {
        if (tunnelSlug && tunnelSlug !== 'audit') {
          cleanSlateProtocol(tunnelSlug, null);
          navigate(`/missions/${tunnelSlug}?new=true`);
        } else { onPaymentSuccess(); }
      };

      if (res.data.admin_bypass) { toast.success('Pack activé (Mode Admin)'); enterFreshTunnel(); }
      else if (res.data.free_pack) { toast.success('Pack gratuit activé !'); enterFreshTunnel(); }
      else if (res.data.free_with_promo) { toast.success('Pack offert par votre partenaire pro !'); enterFreshTunnel(); }
      else if (res.data.paid_with_credits) { toast.success('Payé avec vos crédits de parrainage !'); enterFreshTunnel(); }
      else if (res.data.checkout_url) { window.location.href = res.data.checkout_url; }
      else { toast.error("Erreur: pas d'URL de paiement reçue"); }
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') toast.error(errorMsg);
      else if (Array.isArray(errorMsg)) toast.error(errorMsg.map(e => e.msg || e).join(', '));
      else toast.error('Erreur lors du paiement');
    } finally { setLoading(null); }
  };

  const launchActive = pricing?.launch_active ?? true;
  const pD = pricing?.packs?.mission_demenagement;
  const pH = pricing?.packs?.mission_heritage;

  // V1.2 — Calcul du prix final après code promo (appliqué sur le prix actuel = lancement ou normal)
  const applyPromo = (basePrice) => {
    if (!appliedCoupon || !basePrice) return basePrice;
    const pct = appliedCoupon.percent_off || 0;
    return Math.round(basePrice * (1 - pct / 100) * 100) / 100;
  };

  const formatPrice = (price) => {
    if (!price) return '';
    // Arrondi au centime pour affichage propre
    return Number.isInteger(price) ? `${price.toFixed(0)}€` : `${price.toFixed(2).replace('.', ',')}€`;
  };

  // CCC.13 : pas de carte bundle séparée — toggle "+ Audit (+20€)" sur la carte Déménagement
  const packs = [
    { id: 'mission_audit', name: 'Pack Audit', price: 'Gratuit', priceStrike: null, desc: 'puis 29€ pour lancer',
      features: ['Scan 100% gratuit (CSV/PDF)', 'Détection abonnements oubliés', 'Preview économies avant paiement', '29€ unique pour lancer les résiliations', 'Aucun engagement'],
      highlight: false, color: 'success', icon: Zap },
    { id: 'mission_demenagement', name: 'Mission Déménagement',
      price: pD ? formatPrice(applyPromo(pD.price)) : '129€',
      priceStrike: appliedCoupon && pD
        ? formatPrice(pD.price)
        : (launchActive && pD?.price_normal && pD?.price_launch && pD.price_normal !== pD.price) ? formatPrice(pD.price_normal) : null,
      desc: 'forfait unique',
      features: ['Résiliation anciens contrats', 'Ouverture nouveaux contrats', 'Optimisation tarifs', 'Suivi VIP 30 jours'],
      popular: true, color: 'cyan', icon: Home },
    { id: 'mission_heritage', name: 'Mission Héritage',
      price: pH ? formatPrice(applyPromo(pH.price)) : '149€',
      priceStrike: appliedCoupon && pH
        ? formatPrice(pH.price)
        : (launchActive && pH?.price_normal && pH?.price_launch && pH.price_normal !== pH.price) ? formatPrice(pH.price_normal) : null,
      desc: 'forfait complet',
      features: ['Inventaire complet des contrats', 'Résiliation post-décès', 'Dossier notaire prêt à signer', 'Accompagnement 60 jours'],
      color: 'magenta', icon: Shield }
  ];

  const launchEndFR = pricing?.launch_end
    ? new Date(pricing.launch_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-2">Choisissez votre Mission</h2>
        <p className="text-gray-400 font-mono text-sm">// SÉLECTION_PACK</p>
        {isAdmin && <p className="text-magenta-400 text-xs mt-2 font-mono">// MODE_ADMIN_ACTIF — Paiement bypass</p>}
      </div>

      {launchActive && launchEndFR && (
        <div className="glass-card p-4 border-2 border-orange-400/50 bg-gradient-to-r from-orange-500/10 to-red-500/5 flex items-start md:items-center gap-3 flex-col md:flex-row">
          <Sparkles className="w-6 h-6 text-orange-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-orbitron text-sm font-bold text-orange-300">Offre de lancement active</p>
            <p className="text-gray-300 text-xs md:text-sm">
              Tarifs réduits <span className="text-orange-400 font-bold">jusqu'au {launchEndFR}</span>. Les prix normaux (179€ / 199€) s'appliqueront automatiquement ensuite.
            </p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 md:gap-6">
        {packs.map((pack) => {
          const IconComponent = pack.icon;
          const colorClass = pack.color === 'success' ? 'text-success border-success' : pack.color === 'cyan' ? 'text-cyan-400 border-cyan-400' : 'text-magenta-400 border-magenta-400';
          const bgClass = pack.color === 'success' ? 'bg-success' : pack.color === 'cyan' ? 'bg-cyan-400' : 'bg-magenta-400';
          return (
            <div key={pack.id} className={`glass-card p-4 md:p-6 border-2 ${colorClass.split(' ')[1]}/30 hover:${colorClass.split(' ')[1]} transition-all relative overflow-hidden`}>
              {pack.popular && (<div className="absolute top-0 right-0 bg-cyan-400 text-black px-3 py-1 text-xs font-orbitron font-bold">POPULAIRE</div>)}
              {pack.highlight && (<div className="absolute top-0 right-0 bg-success text-black px-3 py-1 text-xs font-orbitron font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" />AU SUCCES</div>)}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 ${bgClass}/20 rounded-lg flex items-center justify-center`}>
                  <IconComponent className={`w-5 h-5 ${colorClass.split(' ')[0]}`} />
                </div>
                <div>
                  <h3 className={`font-orbitron text-lg font-bold ${colorClass.split(' ')[0]}`}>{pack.name}</h3>
                  <p className="text-gray-500 text-xs">{pack.desc}</p>
                </div>
              </div>
              <div className="mb-2 flex items-baseline gap-2 flex-wrap">
                {/* CCC.14 : si toggle "+Audit" actif sur Déménagement, on affiche 169€ + barré 178€ */}
                {pack.id === 'mission_demenagement' && addAuditToDemenagement ? (
                  <>
                    <span className="font-orbitron text-lg text-gray-500 line-through decoration-2 decoration-red-500/60">{pD ? formatPrice(pD.price + 29) : '178€'}</span>
                    <p className={`font-orbitron text-3xl font-black text-cyan-400`} style={{ textShadow: '0 0 20px rgba(0,245,255,0.5)' }}>
                      {pD ? formatPrice(applyPromo(pD.price + 20)) : '169€'}
                    </p>
                    <span className="font-mono text-[10px] text-success border border-success/40 rounded px-1.5 py-0.5 uppercase">Pack Sérénité +Audit</span>
                  </>
                ) : (
                  <>
                    {pack.priceStrike && (<span className="font-orbitron text-lg text-gray-500 line-through decoration-2 decoration-red-500/60">{pack.priceStrike}</span>)}
                    <p className={`font-orbitron text-3xl font-black ${colorClass.split(' ')[0]}`} style={{ textShadow: `0 0 20px ${pack.color === 'success' ? 'rgba(0,255,148,0.5)' : pack.color === 'cyan' ? 'rgba(0,245,255,0.5)' : 'rgba(255,0,255,0.5)'}` }}>{pack.price}</p>
                  </>
                )}
                {pack.priceStrike && !(pack.id === 'mission_demenagement' && addAuditToDemenagement) && (
                  appliedCoupon && pack.id !== 'mission_audit' ? (
                    <span className="font-mono text-[10px] text-success border border-success/40 rounded px-1.5 py-0.5 uppercase">Code {appliedCoupon.code} −{appliedCoupon.percent_off}%</span>
                  ) : (
                    <span className="font-mono text-[10px] text-orange-400 border border-orange-400/40 rounded px-1.5 py-0.5 uppercase">Lancement 6 mois</span>
                  )
                )}
              </div>
              <p className="text-gray-500 text-[11px] italic mb-4 leading-snug">Frais de résiliation éventuels appliqués par vos fournisseurs non inclus — à votre charge</p>
              <ul className="space-y-2 mb-4">
                {pack.features.map((f, i) => (<li key={i} className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle className={`w-4 h-4 ${colorClass.split(' ')[0]} flex-shrink-0`} />{f}</li>))}
              </ul>

              {/* CCC.13 : toggle "+ Audit Pack (+20€)" UNIQUEMENT sur la carte Déménagement.
                  Si coché, le checkout passe le pack_type bundle au backend (169€). */}
              {pack.id === 'mission_demenagement' && (
                <label
                  htmlFor="add-audit-toggle"
                  className={`flex items-start gap-3 p-3 mb-4 rounded-lg border-2 cursor-pointer transition-all ${
                    addAuditToDemenagement
                      ? 'border-success/60 bg-success/10'
                      : 'border-gray-700 bg-gray-900/40 hover:border-gray-600'
                  }`}
                >
                  <input
                    id="add-audit-toggle"
                    type="checkbox"
                    checked={addAuditToDemenagement}
                    onChange={(e) => setAddAuditToDemenagement(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded accent-success cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className={`font-bold text-sm ${addAuditToDemenagement ? 'text-success' : 'text-white'}`}>
                        + Pack Audit pouvoir d'achat
                      </span>
                      <span className={`font-orbitron text-sm font-black ${addAuditToDemenagement ? 'text-success' : 'text-gray-400'}`}>
                        +20€
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Scan de votre relevé bancaire + résiliation des abonnements en trop. <span className="text-success font-bold">Économise 9€</span> vs séparé.
                    </p>
                  </div>
                </label>
              )}

              <Button onClick={() => handleSelectPack(pack.id)} disabled={loading === pack.id || (!isAdmin && !cgvAccepted)} className={`w-full py-5 text-base font-orbitron ${bgClass} transition-all duration-200 ${!isAdmin && !cgvAccepted ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110'} ${cgvAccepted && !loading ? pack.id === 'mission_audit' ? 'animate-pulse-glow' : pack.id === 'mission_demenagement' ? 'animate-pulse-glow-cyan' : 'animate-pulse-glow-magenta' : ''}`} style={{ color: '#000000', textShadow: 'none' }} data-testid={`select-pack-${pack.id}`}>
                <span className="text-black font-bold">
                  {loading === pack.id
                    ? <Loader2 className="w-5 h-5 animate-spin" />
                    : pack.id === 'mission_demenagement' && addAuditToDemenagement
                      ? 'Lancer la Mission (169€)'
                      : 'Lancer la Mission'}
                </span>
              </Button>
              {/* CCC.1 — bouton "Simuler Succès Paiement" retiré du dashboard client.
                  Désormais uniquement dans Admin → Missions → Activer mission (gratuite).
                  Avant : créait un conflit entre flow Stripe normal et flow bypass. */}
            </div>
          );
        })}
      </div>

      <div className="glass-card p-4 border border-orange-400/40 bg-orange-400/5 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300 leading-relaxed">
          <p className="font-bold text-orange-400 mb-1">Frais de résiliation à votre charge</p>
          <p>Les éventuels frais de résiliation anticipée (engagement, pénalités, soldes dus) appliqués par vos fournisseurs ne sont <span className="font-bold text-white">jamais pris en charge par L'EFFACEUR</span>. Ils vous sont facturés directement par vos fournisseurs selon les conditions de chaque contrat.</p>
        </div>
      </div>

      <div className={`glass-card p-5 border-2 transition-all ${appliedCoupon ? 'border-success/40 bg-success/5' : 'border-cyan-400/20'}`}>
        <div className="flex items-center gap-2 mb-3">
          <Ticket className={`w-5 h-5 ${appliedCoupon ? 'text-success' : 'text-cyan-400'}`} />
          <h3 className="font-orbitron text-sm font-bold text-white">Vous avez un code promo ?</h3>
        </div>
        {appliedCoupon ? (
          <div className="flex items-center justify-between bg-success/10 border border-success/40 rounded-lg px-4 py-3">
            <div>
              <p className="font-mono text-success text-sm font-bold">{appliedCoupon.code} - -{appliedCoupon.percent_off}%</p>
              {appliedCoupon.description && (<p className="text-gray-400 text-xs mt-0.5">{appliedCoupon.description}</p>)}
            </div>
            <button type="button" onClick={handleRemoveCoupon} className="text-gray-400 hover:text-red-400 transition-colors" title="Retirer le code" data-testid="remove-coupon-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <Input type="text" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Ex: PRO-NOTAIRE-X7K9" className="flex-1 bg-gray-800/50 border-gray-700 focus:border-cyan-400 uppercase font-mono" data-testid="coupon-input" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleValidateCoupon(); } }} />
            <Button type="button" variant="outline" onClick={() => handleValidateCoupon()} disabled={couponValidating || !couponInput.trim()} className="border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/10 font-orbitron" data-testid="validate-coupon-btn">
              {couponValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
            </Button>
          </div>
        )}
        <p className="text-gray-500 text-xs mt-2 italic">Parrainage, partenaire pro (notaire, agence immo...) ou offre de lancement.</p>
      </div>

      <div id="cgv-checkbox-section" className={`glass-card p-5 border-2 transition-all ${showCgvError && !cgvAccepted ? 'border-red-500/50 bg-red-500/5' : cgvAccepted ? 'border-success/30 bg-success/5' : 'border-gray-700'}`}>
        <div className="flex items-start gap-3">
          <Checkbox id="cgv-accept" checked={cgvAccepted} onCheckedChange={(checked) => { setCgvAccepted(checked); if (checked) setShowCgvError(false); }} className={`mt-0.5 ${showCgvError && !cgvAccepted ? 'border-red-500' : ''}`} data-testid="cgv-checkbox" />
          <label htmlFor="cgv-accept" className="text-sm text-gray-300 leading-relaxed cursor-pointer">
            J'accepte les{' '}
            <Link to="/cgv" target="_blank" className="text-cyan-400 hover:underline">Conditions Générales de Vente</Link>
            {' '}et je demande l'exécution immédiate de la prestation, reconnaissant que je renonce à mon droit de rétractation une fois le service pleinement exécuté.
          </label>
        </div>
        {showCgvError && !cgvAccepted && (
          <div className="flex items-center gap-2 mt-3 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Vous devez accepter les CGV pour continuer</span>
          </div>
        )}
      </div>
    </div>
  );
};

export { PaymentTab };
